import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';

import type { MultiFactorError } from 'firebase/auth';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedInput } from '../../../components/forms/HookedInput';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { useAuth } from '../../../context/AuthContext';
import { sendExceptionToSentry } from '../../../utils/errors';
import { zodResolver } from '../../../utils/zod';
import { AuthCard } from './AuthCard';
import { AuthLayout } from './AuthLayout';
import { ResetPasswordButton } from './ResetPasswordButton';

type FormValues = {
  email: string;
  password: string;
};

const schema: z.ZodSchema<FormValues> = z.object({
  email: z.email('Invalid email').min(1, 'Email is required'),
  password: z.string().min(1, 'Password is required'),
});

interface Props {
  onMultiFactorError: (error: MultiFactorError) => Promise<void>;
}

export const LoginCard = (props: Props) => {
  const { onMultiFactorError } = props;

  const auth = useAuth();

  const methods = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: zodResolver(schema),
  });

  const setFormError = (message: string) => {
    methods.setError('email', { message: '' });
    methods.setError('password', { message });
  };

  const handleMfaError = async (error: MultiFactorError) => {
    try {
      await onMultiFactorError(error);
    } catch (err) {
      switch (err.code) {
        case 'auth/captcha-check-failed':
          setFormError('reCAPTCHA verification failed. Please reload and try again');
          break;
        case 'auth/too-many-requests':
          setFormError('Too many verification attempts. Please try again later');
          break;
        default:
          setFormError('Service temporarily unavailable. Please contact support');
          return sendExceptionToSentry(err);
      }
    }
  };

  const login = async (data: FormValues) => {
    try {
      await auth.login(data.email, data.password);
    } catch (err) {
      switch (err.code) {
        case 'auth/multi-factor-auth-required':
          return handleMfaError(err);
        case 'auth/user-not-found':
        case 'auth/invalid-credential':
          return setFormError(
            'Incorrect username or password. Double-check your credentials and try again.',
          );
        case 'auth/too-many-requests':
          return setFormError('Too many login attempts. Please try again later');
        default: {
          setFormError('Service temporarily unavailable. Please contact support');
          return sendExceptionToSentry(err);
        }
      }
    }
  };

  return (
    <AuthLayout>
      <AuthCard>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Text fontWeight='semibold' fontSize='2xl'>
            Login
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Enter your email and password below to login to your account
          </ChakraV3.Text>
        </ChakraV3.Stack>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(login)}>
            <ChakraV3.Stack gap={4}>
              <HookedFormField name='email' label='Email'>
                <HookedInput name='email' size='md' placeholder='Enter email' />
              </HookedFormField>
              <HookedFormField name='password' label='Password'>
                <HookedInput
                  name='password'
                  type='password'
                  size='md'
                  placeholder='Enter password'
                />
              </HookedFormField>
              <ResetPasswordButton />
              <HookedSubmitButton size='md' mt={4} disabled={false} loadingText='Logging in...'>
                Login
              </HookedSubmitButton>
            </ChakraV3.Stack>
          </form>
        </FormProvider>
      </AuthCard>
    </AuthLayout>
  );
};
